import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFetch } from "../useFetch";
import type {
	FavoriteResponse,
	CreateFavoritePayload,
	UpdateFavoritePayload,
} from "../../core/types/control";

const FAVORITES_FETCH_COOLDOWN_MS = 1600;
const FAVORITES_INITIAL_DATA: FavoriteResponse[] = [];
const lastRequestAtByClient = new Map<number, number>();
const inFlightRequestByClient = new Map<number, Promise<FavoriteResponse[] | null>>();
const lastSuccessfulDataByClient = new Map<number, FavoriteResponse[]>();

const getFavoritesCacheKey = (clientId: number) => `servix_favorites_client_${clientId}`;

const readFavoritesCache = (clientId: number): FavoriteResponse[] | null => {
	try {
		const raw = window.localStorage.getItem(getFavoritesCacheKey(clientId));
		if (!raw) return null;
		const parsed = JSON.parse(raw) as unknown;
		return Array.isArray(parsed) ? (parsed as FavoriteResponse[]) : null;
	} catch {
		return null;
	}
};

const writeFavoritesCache = (clientId: number, data: FavoriteResponse[]): void => {
	try {
		window.localStorage.setItem(getFavoritesCacheKey(clientId), JSON.stringify(data));
	} catch {
		// Ignore quota/private mode write failures.
	}
};

function isTooManyRequestsError(error: unknown): boolean {
	if (!(error instanceof Error)) return false;
	const message = error.message.toLowerCase();
	return message.includes("429") || message.includes("too many requests");
}

function isNotFoundFavoritesError(error: unknown): boolean {
	if (!(error instanceof Error)) return false;
	const message = error.message.toLowerCase();
	return message.includes("404") || message.includes("no se encontraron");
}

// ============================================
// GET FAVORITES BY CLIENT
// ============================================
export function useFavoritesByClient(clientId: number) {
	const initialDataByClient = useMemo(() => {
		if (!(clientId > 0)) return FAVORITES_INITIAL_DATA;
		const cached = readFavoritesCache(clientId);
		if (Array.isArray(cached)) {
			lastSuccessfulDataByClient.set(clientId, cached);
			return cached;
		}

		return FAVORITES_INITIAL_DATA;
	}, [clientId]);

	const fetch = useFetch<FavoriteResponse[]>({
		endpoint: `/favorites/client/${clientId}`,
		method: "GET",
		auto: false,
		initialData: initialDataByClient,
	});
	const fetchExecute = fetch.execute;
	const fetchReset = fetch.reset;
	const fetchExecuteRef = useRef(fetchExecute);
	const fetchResetRef = useRef(fetchReset);
	const lastSuccessfulDataRef = useRef<FavoriteResponse[] | null>([]);

	useEffect(() => {
		fetchExecuteRef.current = fetchExecute;
	}, [fetchExecute]);

	useEffect(() => {
		fetchResetRef.current = fetchReset;
	}, [fetchReset]);

	useEffect(() => {
		if (Array.isArray(fetch.data)) {
			lastSuccessfulDataRef.current = fetch.data;
			if (clientId > 0) {
				lastSuccessfulDataByClient.set(clientId, fetch.data);
				writeFavoritesCache(clientId, fetch.data);
			}
		}
	}, [clientId, fetch.data]);

	const execute = useCallback(
		async (overrides?: Parameters<typeof fetchExecuteRef.current>[0]) => {
			if (!(clientId > 0)) return [];
			const forceRefresh = typeof overrides !== "undefined";

			const sharedInFlight = inFlightRequestByClient.get(clientId);
			if (sharedInFlight && !forceRefresh) return sharedInFlight;
			if (sharedInFlight && forceRefresh) {
				inFlightRequestByClient.delete(clientId);
			}

			const now = Date.now();
			const lastRequestedAt = lastRequestAtByClient.get(clientId) ?? 0;
			const persistedCache = readFavoritesCache(clientId);
			if (Array.isArray(persistedCache)) {
				lastSuccessfulDataByClient.set(clientId, persistedCache);
				lastSuccessfulDataRef.current = persistedCache;
			}

			const cachedByClient = lastSuccessfulDataByClient.get(clientId) ?? persistedCache;
			if (
				!forceRefresh &&
				now - lastRequestedAt < FAVORITES_FETCH_COOLDOWN_MS &&
				Array.isArray(cachedByClient)
			) {
				return cachedByClient;
			}

			lastRequestAtByClient.set(clientId, now);

			const request = fetchExecuteRef.current(overrides)
				.then((response) => {
					if (Array.isArray(response)) {
						lastSuccessfulDataRef.current = response;
						lastSuccessfulDataByClient.set(clientId, response);
						writeFavoritesCache(clientId, response);
						return response;
					}

					return (
						lastSuccessfulDataByClient.get(clientId) ??
						readFavoritesCache(clientId) ??
						lastSuccessfulDataRef.current ??
						[]
					);
				})
				.catch((error) => {
					if (isTooManyRequestsError(error)) {
						return (
							lastSuccessfulDataByClient.get(clientId) ??
							readFavoritesCache(clientId) ??
							lastSuccessfulDataRef.current ??
							[]
						);
					}

					if (isNotFoundFavoritesError(error)) {
						fetchResetRef.current();
						lastSuccessfulDataRef.current = [];
						lastSuccessfulDataByClient.set(clientId, []);
						writeFavoritesCache(clientId, []);
						return [];
					}

					return (
						lastSuccessfulDataByClient.get(clientId) ??
						readFavoritesCache(clientId) ??
						lastSuccessfulDataRef.current ??
						[]
					);
				})
				.finally(() => {
					inFlightRequestByClient.delete(clientId);
				});

			inFlightRequestByClient.set(clientId, request);
			return request;
		},
		[clientId],
	);

	useEffect(() => {
		if (!(clientId > 0)) {
			fetchResetRef.current();
			return;
		}

		const cached = readFavoritesCache(clientId);
		if (Array.isArray(cached)) {
			lastSuccessfulDataRef.current = cached;
			lastSuccessfulDataByClient.set(clientId, cached);
			fetchResetRef.current();
		}

		void execute().catch(() => null);
	}, [clientId, execute]);

	return { ...fetch, execute };
}

// ============================================
// GET FAVORITE BY ID
// ============================================
export function useFavorite(id: number) {
	return useFetch<FavoriteResponse>({
		endpoint: `/favorites/${id}`,
		method: "GET",
	});
}

// ============================================
// CREATE FAVORITE
// ============================================
export function useCreateFavorite() {
	const fetch = useFetch<FavoriteResponse, CreateFavoritePayload>({
		endpoint: "/favorites",
		method: "POST",
		auto: false,
	});

	const create = (data: CreateFavoritePayload) => {
		return fetch.execute({ body: data });
	};

	return { ...fetch, create };
}

// ============================================
// UPDATE FAVORITE (TOGGLE STATUS)
// ============================================
export function useUpdateFavorite(id: number) {
	const fetch = useFetch<FavoriteResponse, UpdateFavoritePayload>({
		endpoint: `/favorites/${id}`,
		method: "PATCH",
		auto: false,
	});

	const toggle = (isFavorite: boolean, favoriteId?: number) => {
		// El backend lee @Body('isFavorite'); enviamos ambas claves por compatibilidad.
		return fetch.execute({
			endpoint:
				typeof favoriteId === "number" && favoriteId > 0
					? `/favorites/${favoriteId}`
					: `/favorites/${id}`,
			body: { isFavorite, is_favorite: isFavorite },
		});
	};

	return { ...fetch, toggle };
}

// ============================================
// DELETE FAVORITE
// ============================================
export function useDeleteFavorite() {
	const fetch = useFetch<{ message: string }>({
		endpoint: "/favorites",
		method: "DELETE", // Método DELETE estándar
		auto: false,
	});

	const remove = (id: number) => {
		return fetch.execute({ endpoint: `/favorites/${id}` });
	};

	return { ...fetch, remove };
}
